import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/contexts/ConfigContext";
import ManualTransferForm from "@/components/ManualTransferForm";
import AdminMediatorBankCard from "@/components/AdminMediatorBankCard";
import {
  Check, X, Clock, ChevronLeft, ShieldCheck, Lock, Upload, FileText,
  Download, AlertTriangle, HeartHandshake, Video, Building2, Info, Coins, Plus, Star,
} from "lucide-react";

const CLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);

// Orden canónico del timeline visible al solicitante (simplificado feb 2026: se ocultan los pasos
// "sesión adicional pendiente" y "sesión adicional confirmada" — se manejan internamente en el motor
// pero no aparecen en el Estado del Proceso).
const STATE_FLOW = [
  "solicitud_enviada",
  "solicitud_aceptada",  // sinónimo visual — al aceptar pasa directo a pago_inicial_pendiente
  "pago_inicial_pendiente",
  "pago_inicial_confirmado",  // visual — sinónimo de mediacion_en_curso post-pago
  "mediacion_en_curso",
  "cierre_sin_acuerdo_pendiente_documento",
  "cierre_con_acuerdo_pendiente_pago",
  "pago_cierre_confirmado",
  "documento_final_pendiente_aprobacion",
  "pago_pendiente_liberacion",
  "mediacion_completada",
];

const STATE_LABELS = {
  solicitud_enviada: "Solicitud enviada",
  solicitud_aceptada: "Solicitud aceptada",
  pago_inicial_pendiente: "Pago inicial pendiente",
  pago_inicial_confirmado: "Pago inicial confirmado",
  mediacion_en_curso: "Mediación en curso",
  sesion_adicional_pendiente_pago: "Sesión adicional pendiente de pago",
  sesion_adicional_confirmada: "Sesión adicional confirmada",
  cierre_sin_acuerdo_pendiente_documento: "Cierre sin acuerdo pendiente de documento",
  cierre_con_acuerdo_pendiente_pago: "Cierre con acuerdo pendiente de pago",
  pago_cierre_confirmado: "Pago de cierre confirmado",
  documento_final_pendiente_aprobacion: "Documento final pendiente de aprobación",
  pago_pendiente_liberacion: "Pago pendiente de liberación",
  mediacion_completada: "Mediación completada",
  en_revision: "En revisión",
  cancelada: "Cancelada",
};

const DOC_KIND_LABEL = {
  acta_frustrada: "Acta de Mediación Frustrada",
  acta_acuerdo: "Acta de Mediación con Acuerdo",
  comprobante_sesion: "Comprobante de sesión",
};

export default function MediationRequestDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { config, loaded } = useConfig();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // "pay_initial" | "pay_session" | "pay_closing" | "upload_doc" | "review"

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get(`/mediation-requests/${id}`);
      setDoc(data);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, [id]);

  // Refetch silencioso (sin spinner) para usar tras acciones exitosas.
  // Garantiza que la UI vea el estado real del backend (incluyendo side-effects,
  // audit_log, notificaciones y transiciones automáticas de status).
  const refetch = useCallback(async () => {
    try {
      const { data } = await api.get(`/mediation-requests/${id}`);
      setDoc(data);
    } catch (e) { /* silencio: la acción principal ya reporta errores */ }
  }, [id]);

  useEffect(() => { if (loaded) load(); }, [loaded, load]);

  // Auto-refresh cada 10s para reflejar acciones de la contraparte en vivo
  // (sin spinner). Incluye refresh instantáneo al volver a la pestaña.
  useEffect(() => {
    if (!loaded || !id) return;
    const handle = setInterval(refetch, 10000);
    const onVis = () => { if (document.visibilityState === "visible") refetch(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(handle);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [loaded, id, refetch]);

  if (loaded && !config.new_mediation_enabled) return <div className="container-app py-20 text-muted-foreground">Módulo no disponible.</div>;
  if (loading || !doc) return <div className="container-app py-16 text-muted-foreground">{err || "Cargando…"}</div>;

  const isRequester = user?.id === doc.requester_id;
  const isMediator = user?.id === doc.mediator_id;
  const isAdmin = user?.role === "admin";
  const status = doc.status;
  const cancelled = status === "cancelada";
  const inReview = status === "en_revision";
  const finalDone = status === "mediacion_completada";
  const contactVisible = !!doc.mediator_contact;

  const action = async (path, body) => {
    setErr("");
    try {
      const { data } = await api.post(`/mediation-requests/${id}${path}`, body || {});
      // Los pagos manuales devuelven { ok, payment_id, amount_clp, status } y NO la MR
      // completa — sólo hacemos refetch en ese caso. En el resto de las acciones, la
      // respuesta es el doc actualizado y lo aplicamos inmediato.
      if (data && data.id) setDoc(data);
      setModal(null);
      refetch();
      return data;
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); throw e; }
  };

  return (
    <div className="container-app py-8 max-w-5xl" data-testid="mediation-request-detail">
      <button onClick={() => nav(-1)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4">
        <ChevronLeft size={12}/> Volver
      </button>

      {/* Header */}
      <div className="panel p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="label-eyebrow text-cyan-300">Mediación Familiar Privada</div>
            <h1 className="serif text-3xl mt-1">{doc.subject}</h1>
            <div className="mt-2 text-xs text-muted-foreground">
              Solicitante: <strong className="text-foreground">{doc.requester_name}</strong> ({doc.requester_role === "requester" ? "Persona solicitante" : "Abogado/a"}) ·
              Mediador/a: <strong className="text-foreground">{doc.mediator_name}</strong>
            </div>
          </div>
          <StatusPill status={status}/>
        </div>
      </div>

      {err && <div className="mt-4 text-sm text-[hsl(var(--destructive))]" data-testid="mr-error">{err}</div>}

      {doc.pending_validation_kind && (
        <div className="mt-4 panel p-4 border-amber-400/40 bg-amber-400/[0.06]" data-testid="mr-pending-validation">
          <div className="flex items-start gap-3">
            <AlertTriangle size={16} className="text-amber-300 mt-0.5 shrink-0"/>
            <div>
              <div className="text-sm font-semibold text-amber-200">Comprobante en validación</div>
              <div className="text-xs text-muted-foreground mt-1">
                {isRequester
                  ? "Subiste tu comprobante de transferencia. El administrador lo revisará y validará en las próximas horas. Recibirás una notificación cuando quede aprobado."
                  : "El solicitante subió el comprobante de transferencia. Está siendo validado por administración."}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        {/* MAIN column */}
        <div className="lg:col-span-2 space-y-6">
          <Timeline status={status} cancelled={cancelled} inReview={inReview}/>

          {/* Tariff snapshot (frozen) */}
          {doc.tariff_snapshot && <TariffSnapshotCard snapshot={doc.tariff_snapshot} isRequester={isRequester}/>}

          {/* Notes */}
          {doc.notes && (
            <div className="panel p-5">
              <div className="label-eyebrow">Notas del solicitante</div>
              <p className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap">{doc.notes}</p>
            </div>
          )}

          {/* Documents */}
          <DocumentsSection doc={doc} isRequester={isRequester} isMediator={isMediator} onReload={load} setModal={setModal} setErr={setErr}/>

          {/* Sessions */}
          {(doc.sessions || []).length > 0 && (
            <div className="panel p-5">
              <div className="label-eyebrow">Sesiones adicionales</div>
              <ul className="mt-3 space-y-2 text-sm">
                {doc.sessions.map((s, idx) => (
                  <li key={s.id} className="flex items-center justify-between border-b border-white/5 pb-2 last:border-0">
                    <span>Sesión #{idx + 1}</span>
                    <span className={`pill text-xs ${s.paid_at ? "badge-verified" : "badge-pending"}`}>
                      {s.paid_at ? `Pagada · ${CLP(s.mediator_net_clp || 0)}` : "Pendiente de pago"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* SIDE column */}
        <aside className="space-y-6">
          {/* Action panel */}
          <ActionsPanel
            doc={doc} status={status} isRequester={isRequester} isMediator={isMediator} isAdmin={isAdmin}
            onAction={action} setModal={setModal}
          />

          {/* Contact info (hidden until initial_paid) */}
          <div className="panel p-5" data-testid="contact-panel">
            <div className="label-eyebrow inline-flex items-center gap-1"><Lock size={11}/> Datos de contacto</div>
            {contactVisible ? (
              <ul className="mt-3 text-sm space-y-1" data-testid="contact-visible">
                {doc.mediator_contact.office_address && <li className="flex items-start gap-2"><Building2 size={12} className="mt-0.5 text-muted-foreground"/><span>{doc.mediator_contact.office_address}</span></li>}
                {doc.mediator_contact.office_comuna && <li className="text-muted-foreground text-xs">Comuna: {doc.mediator_contact.office_comuna}</li>}
                {doc.mediator_contact.phone && <li>Tel: {doc.mediator_contact.phone}</li>}
                {doc.mediator_contact.email && <li>Correo: {doc.mediator_contact.email}</li>}
              </ul>
            ) : (
              <p className="mt-3 text-xs text-muted-foreground" data-testid="contact-locked">
                Se libera la dirección de oficina, teléfono y correo del mediador al confirmar el Pago Protegido inicial.
              </p>
            )}
          </div>
        </aside>
      </div>

      {/* Timeline de auditoría — sólo admin */}
      {isAdmin && <AdminAuditTimeline auditLog={doc.audit_log || []} doc={doc}/>}

      {/* Modals */}
      {modal === "pay_initial" && <PayInitialModal doc={doc} onClose={() => setModal(null)} onPaid={(receipt) => action("/pay-initial", receipt)}/>}
      {modal === "pay_session" && <PaySessionModal doc={doc} onClose={() => setModal(null)} onPaid={(receipt) => action("/pay-session", receipt)}/>}
      {modal === "pay_closing" && <PayClosingModal mrid={id} onClose={() => setModal(null)} onPaid={(receipt) => action("/pay-closing", receipt)}/>}
      {modal === "review_mediation" && (
        <ReviewMediationModal
          doc={doc}
          onClose={() => setModal(null)}
          onSubmit={(payload) => action(`/documents/${doc.final_doc_id}/review`, payload)}
        />
      )}
      {modal?.startsWith("upload:") && <UploadDocModal kind={modal.split(":")[1]} mrid={id} onClose={() => setModal(null)} onUploaded={load} setErr={setErr}/>}
    </div>
  );
}

function StatusPill({ status }) {
  const label = STATE_LABELS[status] || status;
  let cls = "badge-cyan";
  if (status === "cancelada") cls = "";
  else if (status === "en_revision") cls = "badge-pending";
  else if (status === "mediacion_completada") cls = "badge-verified";
  else if (status.includes("pendiente")) cls = "badge-pending";
  return <span className={`pill ${cls}`} data-testid={`status-pill-${status}`}>{label}</span>;
}

function Timeline({ status, cancelled, inReview }) {
  if (cancelled) return <div className="panel p-5 border-white/10" data-testid="timeline-cancelled"><div className="pill">Cancelada</div><p className="mt-2 text-xs text-muted-foreground">Esta solicitud fue cancelada.</p></div>;
  const currIdx = STATE_FLOW.indexOf(status);
  return (
    <div className="panel p-5" data-testid="mediation-timeline">
      <div className="label-eyebrow">Estado del proceso</div>
      <ol className="mt-4 space-y-2 text-xs">
        {STATE_FLOW.map((s, idx) => {
          const done = currIdx >= idx && !inReview;
          const isCurrent = s === status;
          return (
            <li key={s} className={`flex items-center gap-3 ${done ? "text-foreground" : "text-muted-foreground/60"}`} data-testid={`timeline-step-${s}`}>
              <span className={`w-4 h-4 rounded-full inline-flex items-center justify-center text-[10px] ${done ? "bg-cyan-400 text-cyan-950" : "bg-white/5 border border-white/10"}`}>
                {done ? <Check size={9}/> : idx + 1}
              </span>
              <span className={isCurrent ? "font-medium text-cyan-200" : ""}>{STATE_LABELS[s]}</span>
            </li>
          );
        })}
      </ol>
      {inReview && (
        <div className="mt-3 p-3 rounded-md border border-amber-400/25 bg-amber-400/10 text-amber-200/90 text-xs">
          <AlertTriangle size={12} className="inline mr-1"/> Esta mediación está en revisión administrativa.
        </div>
      )}
    </div>
  );
}

function TariffSnapshotCard({ snapshot, isRequester }) {
  const { config } = useConfig();
  const base = snapshot.valor_base_frustrada_clp || 0;
  const acuerdo = snapshot.valor_acuerdo_total_clp || 0;
  const dynamicFee = Number(config?.business_config?.cargo_fijo_mediacion ?? config?.colega_link_initial_fee_clp ?? 13200);
  const cargoInicial = snapshot.colega_link_initial_fee_clp || snapshot.mediation_fixed_initial_fee_clp || dynamicFee;
  const rate = (snapshot.colega_link_agreement_rate ?? config?.business_config?.porcentaje_comision_acuerdo ?? 0.08);
  const saldo = Math.max(0, acuerdo - base);
  const comision = Math.ceil(saldo * rate);
  const totalFrustrada = base + cargoInicial;
  const totalAcuerdo = totalFrustrada + saldo + comision;
  const cap = snapshot.family_mediation_total_cap_clp || 130602;

  // Vista solicitante: sólo precios finales, sin detalle interno de comisiones Colega Link
  if (isRequester) {
    return (
      <div className="panel p-5" data-testid="tariff-snapshot">
        <div className="flex items-center gap-2"><Lock size={14} className="text-amber-300"/><h3 className="serif text-lg">Tarifario congelado</h3></div>
        <p className="text-xs text-muted-foreground mt-1">Precios acordados al aceptarse la solicitud. Sin sorpresas.</p>
        <ul className="mt-3 space-y-2 text-sm">
          <li className="flex justify-between"><span className="text-muted-foreground">Precio mediación</span><span className="font-medium">{CLP(totalFrustrada)}</span></li>
          <li className="flex justify-between border-t border-white/5 pt-2"><span className="text-muted-foreground">Monto adicional si hay acuerdo</span><span className="font-medium">{CLP(saldo + comision)}</span></li>
          <li className="flex justify-between border-t border-white/5 pt-2"><span className="font-medium">Total si hay acuerdo</span><span className="font-medium text-emerald-300">{CLP(totalAcuerdo)}</span></li>
          <li className="text-xs text-muted-foreground pt-2 border-t border-white/5">Tope legal máximo: <strong className="text-foreground">{CLP(cap)}</strong> · Congelado {snapshot.frozen_at ? `el ${new Date(snapshot.frozen_at).toLocaleDateString("es-CL")}` : ""}</li>
        </ul>
      </div>
    );
  }

  // Vista mediador / admin: desglose completo con comisiones
  return (
    <div className="panel p-5" data-testid="tariff-snapshot">
      <div className="flex items-center gap-2"><Lock size={14} className="text-amber-300"/><h3 className="serif text-lg">Tarifario congelado</h3></div>
      <p className="text-xs text-muted-foreground mt-1">Valores acordados al aceptarse la solicitud. Inmutables durante toda esta mediación.</p>
      <ul className="mt-3 space-y-1 text-sm">
        <li className="flex justify-between"><span className="text-muted-foreground">Honorario base (mediación frustrada)</span><span className="font-medium">{CLP(base)}</span></li>
        <li className="flex justify-between"><span className="text-muted-foreground">Honorario total con acuerdo</span><span className="font-medium">{CLP(acuerdo)}</span></li>
        <li className="flex justify-between"><span className="text-muted-foreground">Saldo pendiente si hay acuerdo</span><span>{CLP(saldo)}</span></li>
        <li className="flex justify-between"><span className="text-muted-foreground">Comisión Colega Link ({Math.round(rate * 100)}%) sobre saldo</span><span>{CLP(comision)}</span></li>
        <li className="text-xs text-muted-foreground pt-1 border-t border-white/5">Cargo fijo Colega Link inicial: <strong className="text-foreground">{CLP(cargoInicial)}</strong> · Tope total solicitante: <strong className="text-foreground">{CLP(cap)}</strong> · Congelado {snapshot.frozen_at ? `el ${new Date(snapshot.frozen_at).toLocaleDateString("es-CL")}` : ""}</li>
      </ul>
    </div>
  );
}

function ActionsPanel({ doc, status, isRequester, isMediator, isAdmin, onAction, setModal }) {
  const hasPendingValidation = !!doc.pending_validation_kind;
  return (
    <div className="panel-glow p-5" data-testid="actions-panel">
      <div className="label-eyebrow text-[hsl(var(--primary))]">Próxima acción</div>

      {status === "solicitud_enviada" && isMediator && (
        <div className="mt-3 space-y-2">
          <button className="btn-primary w-full" onClick={() => onAction("/accept")} data-testid="btn-accept-request">Aceptar solicitud</button>
          <button className="btn-ghost w-full text-[hsl(var(--destructive))]" onClick={() => onAction("/reject")} data-testid="btn-reject-request">Rechazar</button>
          <p className="text-[11px] text-muted-foreground">Al aceptar, se congela el tarifario actual como copia inmutable para esta mediación.</p>
        </div>
      )}

      {status === "solicitud_enviada" && isRequester && (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-muted-foreground">Esperando la respuesta del mediador. Puedes cancelar mientras no haya aceptación.</p>
          <button className="btn-ghost w-full text-xs" onClick={() => onAction("/cancel")} data-testid="btn-cancel-request">Cancelar solicitud</button>
        </div>
      )}

      {(status === "pago_inicial_pendiente" || status === "solicitud_aceptada") && isRequester && !hasPendingValidation && (
        <div className="mt-3 space-y-2">
          <button className="btn-primary w-full inline-flex items-center justify-center gap-2" onClick={() => setModal("pay_initial")} data-testid="btn-open-pay-initial">
            <ShieldCheck size={14}/> Confirmar Pago Protegido inicial
          </button>
          <p className="text-[11px] text-muted-foreground">Al aprobar administración el comprobante, se libera la dirección y contacto del mediador.</p>
        </div>
      )}

      {status === "mediacion_en_curso" && isMediator && (
        <div className="mt-3 space-y-2">
          <button className="btn-ghost w-full inline-flex items-center justify-center gap-2 text-sm" onClick={() => onAction("/request-session")} data-testid="btn-request-session">
            <Plus size={14}/> Solicitar sesión adicional
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button className="btn-ghost text-xs" onClick={() => onAction("/close-no-agreement")} data-testid="btn-close-no-agreement">Cierre sin acuerdo</button>
            <button className="btn-ghost text-xs border-emerald-400/40 text-emerald-300" onClick={() => onAction("/close-with-agreement")} data-testid="btn-close-with-agreement">Cierre con acuerdo</button>
          </div>
        </div>
      )}

      {status === "sesion_adicional_pendiente_pago" && isRequester && !hasPendingValidation && (
        <button className="btn-primary w-full mt-3 inline-flex items-center justify-center gap-2" onClick={() => setModal("pay_session")} data-testid="btn-open-pay-session">
          <Coins size={14}/> Pagar sesión adicional
        </button>
      )}

      {status === "sesion_adicional_confirmada" && isMediator && (
        <div className="mt-3 space-y-2">
          <button className="btn-ghost w-full text-sm" onClick={() => onAction("/request-session")} data-testid="btn-request-session-more">
            <Plus size={12} className="inline mr-1"/> Solicitar otra sesión
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button className="btn-ghost text-xs" onClick={() => onAction("/close-no-agreement")} data-testid="btn-close-no-agreement-2">Cierre sin acuerdo</button>
            <button className="btn-ghost text-xs border-emerald-400/40 text-emerald-300" onClick={() => onAction("/close-with-agreement")} data-testid="btn-close-with-agreement-2">Cierre con acuerdo</button>
          </div>
        </div>
      )}

      {status === "cierre_sin_acuerdo_pendiente_documento" && isMediator && (
        <div className="mt-3 space-y-2">
          <button className="btn-primary w-full inline-flex items-center justify-center gap-2" onClick={() => setModal("upload:acta_frustrada")} data-testid="btn-upload-acta-frustrada">
            <Upload size={14}/> Subir Acta Frustrada
          </button>
        </div>
      )}

      {status === "cierre_con_acuerdo_pendiente_pago" && isRequester && !hasPendingValidation && (
        <button className="btn-primary w-full mt-3 inline-flex items-center justify-center gap-2" onClick={() => setModal("pay_closing")} data-testid="btn-open-pay-closing">
          <ShieldCheck size={14}/> Pagar cierre con acuerdo
        </button>
      )}

      {status === "pago_cierre_confirmado" && isMediator && (
        <button className="btn-primary w-full mt-3 inline-flex items-center justify-center gap-2" onClick={() => setModal("upload:acta_acuerdo")} data-testid="btn-upload-acta-acuerdo">
          <Upload size={14}/> Subir Acta con Acuerdo
        </button>
      )}

      {/* Contador de vencimiento 24h — visible al solicitante Y al admin */}
      {status === "documento_final_pendiente_aprobacion" && (isRequester || isAdmin) && (
        <ActaDeadlineNotice doc={doc} isAdmin={isAdmin}/>
      )}

      {status === "documento_final_pendiente_aprobacion" && isRequester && (
        <div className="mt-3 space-y-2" data-testid="requester-review-panel">
          <button className="btn-primary w-full" onClick={() => setModal("review_mediation")} data-testid="btn-approve-doc">
            Aprobar mediación · Calificar mediador
          </button>
          <button className="btn-ghost w-full text-xs" onClick={() => {
            const note = window.prompt("Motivo de la revisión (opcional):", "");
            if (note !== null) onAction(`/documents/${doc.final_doc_id}/review`, { approve: false, note });
          }} data-testid="btn-open-review">Abrir revisión administrativa</button>
        </div>
      )}

      {status === "pago_pendiente_liberacion" && (
        <div className="mt-3 space-y-2">
          {doc.auto_released && (
            <div className="rounded-lg border border-emerald-400/40 bg-emerald-400/10 p-3 text-xs" data-testid="mr-auto-released-banner">
              <div className="inline-flex items-center gap-1.5 text-emerald-300 font-semibold">
                <Check size={12}/> Pago liberado automáticamente
              </div>
              <div className="text-muted-foreground mt-1">
                {doc.auto_released_at ? new Date(doc.auto_released_at).toLocaleString("es-CL") : ""} — Administración realizará la transferencia bancaria al mediador.
              </div>
            </div>
          )}
          {isAdmin ? (
            <div className="grid md:grid-cols-2 gap-3 items-start" data-testid="admin-release-payout-grid">
              <div>
                <button className="btn-primary w-full" onClick={() => onAction("/admin-release")} data-testid="btn-admin-release">Aprobar Liberación de Pago</button>
                <p className="text-[11px] text-muted-foreground mt-2">Antes de confirmar, realiza la transferencia al mediador con los datos bancarios que ves a la derecha.</p>
              </div>
              <AdminMediatorBankCard bank={doc.admin_mediator_bank}/>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Documento aprobado. Administración realizará la transferencia al mediador en las próximas horas hábiles.</p>
          )}
        </div>
      )}

      {status === "en_revision" && isAdmin && (
        <div className="mt-3 grid md:grid-cols-2 gap-3 items-start">
          <button className="btn-primary w-full" onClick={() => onAction("/admin-release")} data-testid="btn-admin-release-review">Cerrar revisión y liberar pago</button>
          <AdminMediatorBankCard bank={doc.admin_mediator_bank}/>
        </div>
      )}

      {/* Registro histórico: cuando la mediación está completada, admin mantiene los datos bancarios visibles */}
      {isAdmin && (status === "completada" || status === "cancelada") && doc.admin_mediator_bank && (
        <div className="mt-3" data-testid="admin-bank-history">
          <div className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wider">Registro histórico · cuenta pagada</div>
          <AdminMediatorBankCard bank={doc.admin_mediator_bank} compact/>
        </div>
      )}

      {status === "mediacion_completada" && (
        <div className="mt-3 text-xs text-emerald-300 inline-flex items-center gap-1"><Check size={12}/> Mediación completada — Pago liberado</div>
      )}
    </div>
  );
}

function DocumentsSection({ doc, isRequester, isMediator, setModal, setErr }) {
  const docs = doc.documents || [];
  const canUploadSession = isMediator && ["mediacion_en_curso", "sesion_adicional_confirmada"].includes(doc.status);

  const download = async (d) => {
    try {
      const { data } = await api.get(`/mediation-requests/${doc.id}/documents/${d.id}`);
      const link = document.createElement("a");
      link.href = `data:${data.mime};base64,${data.data_b64}`;
      link.download = data.filename || "documento";
      link.click();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="panel p-5" data-testid="documents-section">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow inline-flex items-center gap-1"><FileText size={11}/> Documentación de cierre</div>
        {canUploadSession && (
          <button className="btn-ghost text-xs" onClick={() => setModal("upload:comprobante_sesion")} data-testid="btn-upload-comprobante">
            <Upload size={11} className="inline mr-1"/> Comprobante sesión
          </button>
        )}
      </div>
      {docs.length === 0 ? (
        <p className="text-xs text-muted-foreground mt-3" data-testid="docs-empty">Aún no hay documentos cargados.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center justify-between border-b border-white/5 pb-2 last:border-0" data-testid={`doc-row-${d.id}`}>
              <div className="text-sm">
                <div>{DOC_KIND_LABEL[d.kind] || d.kind} · <span className="text-xs text-muted-foreground">{d.filename}</span></div>
                <div className="text-[10px] text-muted-foreground">Subido {new Date(d.uploaded_at).toLocaleString("es-CL")}</div>
              </div>
              <div className="flex items-center gap-2">
                {d.review_status && <span className={`pill text-[10px] ${d.review_status === "approved" ? "badge-verified" : d.review_status === "in_review" ? "badge-pending" : ""}`}>{d.review_status === "approved" ? "Aprobado" : d.review_status === "in_review" ? "En revisión" : "Pendiente"}</span>}
                <button className="btn-ghost text-xs inline-flex items-center gap-1" onClick={() => download(d)} data-testid={`doc-download-${d.id}`}>
                  <Download size={11}/> Descargar
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {isRequester && (
        <p className="text-[11px] text-muted-foreground mt-3 flex items-start gap-1.5">
          <Info size={11} className="text-muted-foreground mt-0.5 shrink-0"/>
          Los documentos son visibles sólo para las partes de esta mediación y administración autorizada.
        </p>
      )}
    </div>
  );
}

// ============================ MODALES ============================

function Modal({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid={testid}>
      <div className="panel max-w-md w-full p-6 max-h-[90vh] overflow-y-auto my-4" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="ml-auto flex text-muted-foreground hover:text-foreground" aria-label="Cerrar"><X size={16}/></button>
        {children}
      </div>
    </div>
  );
}

function PayInitialModal({ doc, onClose, onPaid }) {
  const { config } = useConfig();
  const ts = doc.tariff_snapshot || {};
  const base = ts.valor_base_frustrada_clp || 0;
  const dynamicFee = Number(config?.business_config?.cargo_fijo_mediacion ?? config?.colega_link_initial_fee_clp ?? 13200);
  const fixed = ts.colega_link_initial_fee_clp || ts.mediation_fixed_initial_fee_clp || dynamicFee;
  const total = base + fixed;
  return (
    <Modal onClose={onClose} testid="pay-initial-modal">
      <div className="label-eyebrow text-cyan-300">Pago inicial · Transferencia manual</div>
      <h2 className="serif text-2xl mt-2">Confirmar Pago Protegido inicial</h2>
      <ul className="mt-4 space-y-1.5 text-sm">
        <li className="flex justify-between"><span>Honorario base del mediador</span><span className="font-medium">{CLP(base)}</span></li>
        <li className="flex justify-between"><span>Cargo fijo Colega Link</span><span className="font-medium">{CLP(fixed)}</span></li>
        <li className="flex justify-between border-t border-white/10 pt-2 mt-2">
          <span className="serif text-base">Total a transferir</span>
          <span className="serif text-base brand-gradient-text" data-testid="pay-initial-total">{CLP(total)}</span>
        </li>
      </ul>
      <ManualTransferForm
        amount={total}
        referenceLabel={`Mediación · Pago inicial`}
        submitLabel={`Enviar comprobante (${CLP(total)})`}
        onCancel={onClose}
        onSubmit={onPaid}
      />
    </Modal>
  );
}

function PaySessionModal({ doc, onClose, onPaid }) {
  const net = doc.tariff_snapshot?.valor_sesion_adicional_clp || 0;
  return (
    <Modal onClose={onClose} testid="pay-session-modal">
      <div className="label-eyebrow text-cyan-300">Sesión adicional · Transferencia manual</div>
      <h2 className="serif text-2xl mt-2">Pagar sesión adicional</h2>
      <ul className="mt-4 space-y-1.5 text-sm">
        <li className="flex justify-between"><span>Honorario del mediador</span><span className="font-medium">{CLP(net)}</span></li>
        <li className="flex justify-between"><span>Cargo Colega Link</span><span className="font-medium">Sin cargo</span></li>
        <li className="flex justify-between border-t border-white/10 pt-2 mt-2">
          <span className="serif text-base">Total a transferir</span>
          <span className="serif text-base brand-gradient-text" data-testid="pay-session-total">{CLP(net)}</span>
        </li>
      </ul>
      <ManualTransferForm
        amount={net}
        referenceLabel="Mediación · Sesión adicional"
        submitLabel={`Enviar comprobante (${CLP(net)})`}
        onCancel={onClose}
        onSubmit={onPaid}
      />
    </Modal>
  );
}

function PayClosingModal({ mrid, onClose, onPaid }) {
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/mediation-requests/${mrid}/closing-quote`)
      .then(({ data }) => setQuote(data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));
  }, [mrid]);
  const total = quote?.pago_final_si_acuerdo_clp ?? quote?.total_solicitante_clp;
  return (
    <Modal onClose={onClose} testid="pay-closing-modal">
      <div className="label-eyebrow text-emerald-300">Cierre con acuerdo · Transferencia manual</div>
      <h2 className="serif text-2xl mt-2">Pagar saldo de cierre</h2>
      {err && <div className="mt-3 text-sm text-[hsl(var(--destructive))]">{err}</div>}
      {!quote ? <div className="mt-5 text-muted-foreground text-sm">Cargando desglose…</div> : (
        <>
          <ul className="mt-4 space-y-1.5 text-sm">
            <li className="flex justify-between"><span className="text-muted-foreground">Honorario base ya pagado</span><span>{CLP(quote.base_clp ?? quote.mediator_net_base_clp)}</span></li>
            <li className="flex justify-between"><span className="text-muted-foreground">Honorario total con acuerdo</span><span>{CLP(quote.acuerdo_total_clp ?? quote.mediator_total_net_clp)}</span></li>
            <li className="flex justify-between"><span className="text-muted-foreground">Saldo pendiente al mediador</span><span className="font-medium">{CLP(quote.saldo_pendiente_clp ?? quote.mediator_net_closing_clp)}</span></li>
            <li className="flex justify-between"><span>Comisión Colega Link ({Math.round(quote.rate_pct ?? quote.colega_pct ?? 8)}% sobre saldo)</span><span className="font-medium">{CLP(quote.comision_acuerdo_colega_link_clp ?? quote.colega_gross_clp)}</span></li>
            <li className="flex justify-between border-t border-white/10 pt-2 mt-2">
              <span className="serif text-base">Total a transferir</span>
              <span className="serif text-base brand-gradient-text" data-testid="pay-closing-total">{CLP(total)}</span>
            </li>
          </ul>
          <ManualTransferForm
            amount={total}
            referenceLabel="Mediación · Cierre con acuerdo"
            submitLabel={`Enviar comprobante (${CLP(total)})`}
            onCancel={onClose}
            onSubmit={onPaid}
          />
        </>
      )}
    </Modal>
  );
}

function UploadDocModal({ kind, mrid, onClose, onUploaded, setErr }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [localErr, setLocalErr] = useState("");
  const label = DOC_KIND_LABEL[kind] || kind;

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true); setLocalErr("");
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result.split(",")[1]);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      await api.post(`/mediation-requests/${mrid}/documents`, { kind, filename: file.name, mime: file.type || "application/pdf", data_b64: b64 });
      onUploaded();
      onClose();
    } catch (e2) {
      const msg = formatApiError(e2.response?.data?.detail) || e2.message;
      setLocalErr(msg); if (setErr) setErr(msg);
    } finally { setUploading(false); }
  };

  return (
    <Modal onClose={onClose} testid="upload-doc-modal">
      <div className="label-eyebrow text-cyan-300">{kind === "comprobante_sesion" ? "Comprobante de sesión" : "Documento final"}</div>
      <h2 className="serif text-2xl mt-2">Subir {label}</h2>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <input type="file" required onChange={(e) => setFile(e.target.files?.[0] || null)} className="field w-full text-sm" accept=".pdf,image/*" data-testid="upload-doc-file"/>
        {localErr && <div className="text-sm text-[hsl(var(--destructive))]" data-testid="upload-doc-error">{localErr}</div>}
        <p className="text-[11px] text-muted-foreground">PDF o imagen (máx 15 MB). Visible sólo para las partes y administración autorizada.</p>
        <div className="flex gap-2">
          <button type="submit" className="btn-primary flex-1" disabled={uploading || !file} data-testid="upload-doc-confirm">
            {uploading ? "Subiendo…" : "Subir documento"}
          </button>
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
        </div>
      </form>
    </Modal>
  );
}


/* ═════════════════════════════════════════════════════════════
 *  Timeline de auditoría · sólo visible para admin
 *  Renderiza el audit_log[] de la mediación como línea de tiempo,
 *  con etiquetas humanas de cada transición y el actor abreviado.
 * ═════════════════════════════════════════════════════════════ */
const AUDIT_ACTION_LABELS = {
  created: "Solicitud creada",
  accept: "Mediador aceptó",
  "pay-initial": "Pago inicial confirmado",
  "pay-session": "Pago sesión adicional",
  "pay-closing": "Pago de cierre",
  request_session: "Sesión adicional solicitada",
  close_without_agreement: "Cierre sin acuerdo",
  close_with_agreement: "Cierre con acuerdo",
  approve_document: "Documento aprobado",
  reject_document: "Documento rechazado",
  release_payment: "Pago liberado al mediador",
  cancel: "Mediación cancelada",
};

function _fmtAuditAction(a) {
  if (!a) return "—";
  if (a.startsWith("downloaded:")) return `Descargó documento (${a.split(":")[1].slice(0, 6)}…)`;
  if (a.startsWith("uploaded:")) return `Subió documento (${a.split(":")[1]})`;
  const key = a.split(":")[0];
  return AUDIT_ACTION_LABELS[key] || AUDIT_ACTION_LABELS[a] || a;
}

function _fmtAuditWho(actor_id, doc) {
  if (!actor_id) return "sistema";
  if (actor_id === doc.requester_id) return `${doc.requester_name || "Solicitante"} · solicitante`;
  if (actor_id === doc.mediator_id) return `${doc.mediator_name || "Mediador"} · mediador`;
  return `Admin · ${actor_id.slice(0, 8)}`;
}

function AdminAuditTimeline({ auditLog, doc }) {
  const list = [...(auditLog || [])].reverse(); // más reciente primero
  return (
    <section className="mt-10 panel p-5 border-amber-400/30" data-testid="admin-audit-timeline">
      <div className="flex items-center gap-2">
        <Clock size={14} className="text-amber-300"/>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-amber-300">
          Historial de acciones · vista admin
        </h3>
      </div>
      {list.length === 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">Sin eventos registrados aún en el audit log.</p>
      ) : (
        <ol className="mt-4 relative border-l border-white/10 pl-5 space-y-3">
          {list.map((ev, i) => (
            <li key={i} className="relative" data-testid={`audit-tl-${i}`}>
              <span className="absolute -left-[7px] top-1.5 w-2.5 h-2.5 rounded-full bg-amber-400/70 border-2 border-black"/>
              <div className="text-sm">{_fmtAuditAction(ev.action)}</div>
              <div className="text-[11px] text-muted-foreground">
                {ev.at ? new Date(ev.at).toLocaleString("es-CL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                {" · "}
                <span className="text-foreground/80">{_fmtAuditWho(ev.actor_id, doc)}</span>
              </div>
            </li>
          ))}
        </ol>
      )}
      <p className="mt-4 text-[11px] text-muted-foreground italic">
        Este panel es visible sólo para administradores. Consulta la Auditoría global en <code className="text-cyan-300">/app/admin/auditoria</code>.
      </p>
    </section>
  );
}


// ─── Aviso de vencimiento del acta (24h auto-approval) ─────────
function ActaDeadlineNotice({ doc, isAdmin }) {
  const submitted = doc.acta_submitted_at ? new Date(doc.acta_submitted_at) : null;
  if (!submitted || Number.isNaN(submitted.getTime())) return null;
  const deadline = new Date(submitted.getTime() + 24 * 60 * 60 * 1000);
  const remainingMs = deadline.getTime() - Date.now();
  const hoursLeft = Math.max(0, Math.floor(remainingMs / 3600000));
  const minutesLeft = Math.max(0, Math.floor((remainingMs % 3600000) / 60000));
  const deadlineStr = deadline.toLocaleString("es-CL", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
  const critical = hoursLeft < 4;
  return (
    <div className={`rounded-md border p-3 text-xs mb-2 ${critical ? "border-red-400/50 bg-red-400/[0.08] text-red-100" : "border-amber-400/40 bg-amber-400/[0.08] text-amber-100"}`} data-testid="acta-deadline-notice">
      <div className={`inline-flex items-center gap-1.5 font-semibold ${critical ? "text-red-300" : "text-amber-300"}`}>
        <AlertTriangle size={12}/> {isAdmin ? "Aprobación por omisión en curso" : "Atención · Aprobación con plazo"}
      </div>
      <div className="mt-1 leading-relaxed">
        {isAdmin ? (
          <>
            El solicitante <strong>{doc.requester_name}</strong> tiene hasta el <strong>{deadlineStr}</strong> para revisar el acta.
            Si no lo hace, se aprobará por omisión en <strong>{hoursLeft}h {minutesLeft}m</strong>.
          </>
        ) : (
          <>
            Tienes hasta el <strong>{deadlineStr}</strong> para revisar esta acta.
            Si no lo haces, se dará por aprobada automáticamente ({hoursLeft}h {minutesLeft}m restantes).
          </>
        )}
      </div>
    </div>
  );
}

// ─── Cuadro de datos bancarios: implementado en @/components/AdminMediatorBankCard ───

// ─── Modal de calificación 5★ obligatoria al cerrar la mediación ───
function ReviewMediationModal({ doc, onClose, onSubmit }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const canSubmit = rating >= 1 && rating <= 5 && comment.trim().length >= 5;

  const submit = async () => {
    if (!canSubmit) { setErr("Debes seleccionar 1 a 5 estrellas y escribir al menos 5 caracteres."); return; }
    setSaving(true); setErr("");
    try {
      await onSubmit({ approve: true, rating, comment: comment.trim() });
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "No se pudo aprobar.");
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} testid="review-mediation-modal">
      <div className="label-eyebrow text-[hsl(var(--accent))]">Cierre de mediación</div>
      <h2 className="serif text-2xl mt-2">Califica al mediador</h2>
      <p className="mt-3 text-sm text-muted-foreground">
        Al aprobar el documento final, se libera el pago del mediador y esta reseña queda pública en su perfil.
      </p>
      <p className="mt-3 p-3 rounded-md border border-cyan-400/25 bg-cyan-400/[0.05] text-xs text-cyan-100">
        Tu calificación ayuda a que otros colegas confíen y mantiene la calidad de ColegaLink.
      </p>
      <div className="mt-5">
        <div className="label-eyebrow mb-2">Calificación · obligatoria *</div>
        <div className="flex items-center gap-2">
          {[1,2,3,4,5].map((n) => (
            <button
              type="button" key={n} onClick={() => setRating(n)}
              className={`w-10 h-10 rounded-md inline-flex items-center justify-center border ${n <= rating ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-transparent" : "border-[hsl(var(--border))] text-muted-foreground"}`}
              data-testid={`mr-rating-${n}`}
            ><Star size={14}/></button>
          ))}
        </div>
      </div>
      <div className="mt-4">
        <label className="label-eyebrow">Comentario · obligatorio *</label>
        <textarea
          className="field mt-2" rows={3} required minLength={5}
          placeholder="Cuenta cómo fue la experiencia (mínimo 5 caracteres)."
          value={comment} onChange={(e) => setComment(e.target.value)}
          data-testid="mr-review-comment"
        />
        <div className="mt-1 text-[10px] text-muted-foreground text-right">{comment.trim().length}/5+ caracteres</div>
      </div>
      {err && <div className="mt-4 text-sm text-[hsl(var(--destructive))]" data-testid="mr-review-err">{err}</div>}
      <div className="mt-6 flex gap-3">
        <button
          onClick={submit} disabled={saving || !canSubmit}
          className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="mr-review-submit"
        >
          {saving ? "Guardando…" : "Aprobar mediación y liberar pago"}
        </button>
        <button onClick={onClose} className="btn-ghost" disabled={saving}>Cancelar</button>
      </div>
    </Modal>
  );
}
